import { Platform } from 'react-native';
import {
  ScanHttpError,
  type ScanStreamEvent,
} from './scanStreamTypes';
import { parseSseStream } from './sseParser';

/**
 * Streaming image scan + segmentation client — consumes Server-Sent
 * Events from `POST /upload/scan/stream` and yields each event as a
 * strongly-typed {@link ScanStreamEvent}.
 *
 * Event types emitted (interleaved, NOT batched):
 *   status → item (×N) → segmentation_started → mask (×N)
 *     → item_error? (×M) → mask_error? (×K) → summary
 *     → segmentation_complete → complete
 *
 * This file is parser-only — the string-based `event:` frame header
 * is yielded verbatim as the event `type`, so new event names ship
 * without any code change here (just extend the union in
 * `scanStreamTypes.ts`).
 *
 * Web-only in this build. React Native's fetch polyfill in RN 0.72
 * doesn't expose `Response.body` as a ReadableStream, so the
 * `pipeThrough` / `getReader` parse path below isn't available there.
 * Follow-up PR will wire `react-native-sse` for native when needed.
 *
 * Usage:
 *
 *   for await (const evt of streamScan(file, { signal })) {
 *     switch (evt.type) { … }
 *   }
 *
 * Errors:
 *   - Pre-stream 4xx/5xx → throws {@link ScanHttpError} (consumer
 *     branches on `err instanceof ScanHttpError`).
 *   - Mid-stream failure → yielded as `{ type: 'error', data: … }`
 *     events. Items already yielded remain persisted server-side.
 *   - Abort via `opts.signal` stops iteration cleanly; no rollback.
 */

const API_BASE_URL =
  process.env.EXPO_PUBLIC_API_BASE_URL || 'https://backend.remembr.xyz';

const AUTH_TOKEN_STORAGE_KEY = 'auth_token';

interface FileInput {
  uri: string;
  name?: string;
  type?: string;
}

interface StreamScanOptions {
  location?: string;
  description?: string;
  signal?: AbortSignal;
}

/**
 * Read the JWT out of the same storage slot the axios instance uses.
 * On web we pull directly from localStorage; on other platforms we
 * throw the web-only error earlier so we never reach this path.
 */
const getWebAuthToken = (): string | null => {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  return window.localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
};

/**
 * Resolve a local/blob URI to a browser `File` so `FormData` sends
 * bytes the backend can actually read. Mirrors the logic used by the
 * retired `api.uploadContent` for parity.
 */
const toFormFile = async (input: FileInput | File): Promise<File | Blob> => {
  if (typeof File !== 'undefined' && input instanceof File) return input;
  const fi = input as FileInput;
  const resp = await fetch(fi.uri);
  const blob = await resp.blob();
  if (typeof File !== 'undefined') {
    return new File([blob], fi.name || 'image.jpg', {
      type: fi.type || blob.type || 'image/jpeg',
    });
  }
  return blob;
};

export async function* streamScan(
  file: FileInput | File,
  opts: StreamScanOptions = {},
): AsyncGenerator<ScanStreamEvent, void, void> {
  if (Platform.OS !== 'web') {
    throw new Error('Scan streaming is web-only in this build');
  }

  const token = getWebAuthToken();
  const form = new FormData();
  const formFile = await toFormFile(file);
  form.append('file', formFile as any);
  if (opts.location) form.append('location', opts.location);
  if (opts.description) form.append('description', opts.description);

  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}/upload/scan/stream`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: form as any,
      signal: opts.signal,
    });
  } catch (err: any) {
    // Network-level failure before the stream could start — rethrow
    // as-is so the consumer's outer catch can surface a generic
    // connectivity message.
    throw err;
  }

  if (!res.ok) {
    let detail = res.statusText || 'Request failed';
    try {
      const body = await res.json();
      if (body?.detail) {
        detail = Array.isArray(body.detail)
          ? body.detail
              .map((d: any) => d?.msg || JSON.stringify(d))
              .join(', ')
          : String(body.detail);
      }
    } catch {
      /* keep statusText fallback */
    }
    throw new ScanHttpError(res.status, detail);
  }

  if (!res.body) {
    throw new Error(
      'Streaming scan response had no body (browser support gap).',
    );
  }

  const textStream = res.body.pipeThrough(new TextDecoderStream());
  for await (const frame of parseSseStream(textStream)) {
    yield {
      type: frame.event as ScanStreamEvent['type'],
      data: frame.data as any,
    } as ScanStreamEvent;
  }
}

/**
 * FE #124.6 — Resume / re-segment an EXISTING file_record via
 * `POST /upload/{file_record_id}/segment/stream`. Same SSE event
 * shape as `streamScan`, so the consumer's switch-case is identical.
 *
 * Used for:
 *  - Resuming an IN_PROGRESS scan that the user navigated away from
 *  - Retrying a FAILED scan (per backend#313 — idempotent on FAILED;
 *    BE clears pre-confirm inventory_items before replaying)
 *
 * No FormData — the file_record_id is in the URL path; the BE
 * already has the photo from the original upload. Saves bandwidth +
 * time vs re-uploading.
 */
export async function* streamSegmentResume(
  fileRecordId: number,
  opts: { signal?: AbortSignal } = {},
): AsyncGenerator<ScanStreamEvent, void, void> {
  if (Platform.OS !== 'web') {
    throw new Error('Scan streaming is web-only in this build');
  }

  const token = getWebAuthToken();
  let res: Response;
  try {
    res = await fetch(
      `${API_BASE_URL}/upload/${fileRecordId}/segment/stream`,
      {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        signal: opts.signal,
      },
    );
  } catch (err: any) {
    throw err;
  }

  if (!res.ok) {
    let detail = res.statusText || 'Request failed';
    try {
      const body = await res.json();
      if (body?.detail) {
        detail = Array.isArray(body.detail)
          ? body.detail
              .map((d: any) => d?.msg || JSON.stringify(d))
              .join(', ')
          : String(body.detail);
      }
    } catch {
      /* keep statusText fallback */
    }
    throw new ScanHttpError(res.status, detail);
  }

  if (!res.body) {
    throw new Error(
      'Streaming segment response had no body (browser support gap).',
    );
  }

  const textStream = res.body.pipeThrough(new TextDecoderStream());
  for await (const frame of parseSseStream(textStream)) {
    yield {
      type: frame.event as ScanStreamEvent['type'],
      data: frame.data as any,
    } as ScanStreamEvent;
  }
}

export { ScanHttpError } from './scanStreamTypes';
export type { ScanStreamEvent } from './scanStreamTypes';
